import React, { useState, useEffect, useRef } from "react";
import { buildApiUrl } from "../utils/apiConfig";
import { PageResponse, PaginationParams, buildPaginationQuery } from "../types/pagination";
import { featuredVideoAnalytics, WORDPRESS_TAB } from "../services/courseEventService";
import VideoLightbox from "./VideoLightbox";
import { decodeHtmlEntities } from "../utils/htmlUtils";
import Pagination from "./Pagination";
import "../styles/featured-content.css";

interface FeaturedContentItem {
    id: number;
    slug: string;
    title: string;
    featuredContentImageUrl: string;
    subtitle: string;
    linkLabel: string;
    linkUrl: string;
}

interface NilEduFeaturedContentGridProps {
    limit?: number;
    showPagination?: boolean; // New prop to control pagination display
}

export default function NilEduFeaturedContent({ limit = 3, showPagination = false }: NilEduFeaturedContentGridProps) {
    const [pageData, setPageData] = useState<PageResponse<FeaturedContentItem> | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<{ id: number; title: string; vimeoUrl: string } | null>(null);
    const [imageLoadingStates, setImageLoadingStates] = useState<Record<number, boolean>>({});
    const [imageErrorStates, setImageErrorStates] = useState<Record<number, boolean>>({});
    const contentRef = useRef<HTMLDivElement>(null);

    const ITEMS_PER_PAGE = limit;

    useEffect(() => {
        const fetchFeaturedContent = async () => {
            try {
                setError(null);

                // Build pagination parameters
                const paginationParams: PaginationParams = {
                    page: currentPage,
                    size: ITEMS_PER_PAGE
                };
                const queryString = buildPaginationQuery(paginationParams);

                const response = await fetch(
                    buildApiUrl(`/api/nil-edu/featuredContent?${queryString}`),
                    {
                        headers: { Accept: "application/json" },
                        credentials: "include" // Use HTTP-only cookie for auth
                    }
                );

                if (!response.ok) {
                    throw new Error(`Failed to fetch featured content: ${response.status}`);
                }

                const responseData = await response.json();

                // Handle both plain array and paginated response formats
                let pageResponse: PageResponse<FeaturedContentItem>;

                if (Array.isArray(responseData)) {
                    // Backend returned a plain array, slice it to respect the page size
                    const startIndex = currentPage * ITEMS_PER_PAGE;
                    const endIndex = startIndex + ITEMS_PER_PAGE;
                    const paginatedContent = responseData.slice(startIndex, endIndex);
                    const totalPages = Math.ceil(responseData.length / ITEMS_PER_PAGE);

                    pageResponse = {
                        content: paginatedContent,
                        pageable: {
                            pageNumber: currentPage,
                            pageSize: ITEMS_PER_PAGE,
                            offset: startIndex,
                            sort: { sorted: false, unsorted: true, empty: true },
                            paged: true,
                            unpaged: false
                        },
                        totalPages: totalPages,
                        totalElements: responseData.length,
                        last: currentPage >= totalPages - 1,
                        first: currentPage === 0,
                        size: ITEMS_PER_PAGE,
                        number: currentPage,
                        numberOfElements: paginatedContent.length,
                        empty: paginatedContent.length === 0
                    };
                } else {
                    // Backend returned proper PageResponse format
                    pageResponse = responseData as PageResponse<FeaturedContentItem>;
                }

                setPageData(pageResponse);

                // Initialize loading states for all images
                const initialLoadingStates: Record<number, boolean> = {};
                pageResponse.content.forEach((item: FeaturedContentItem) => {
                    initialLoadingStates[item.id] = false;
                });
                setImageLoadingStates(initialLoadingStates);
            } catch (err) {
                console.error("Error fetching NIL EDU featured content:", err);
                setError("Failed to load featured content. Please try again later.");
            } finally {
                setInitialLoading(false);
            }
        };

        fetchFeaturedContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, ITEMS_PER_PAGE]);

    // Handler for page changes
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Helper function to check if a URL is a video URL
    const isVideoUrl = (url: string | null): boolean => {
        if (!url) return false;
        const videoPatterns = [
            /vimeo\.com/i,
            /wistia\.com/i,
            /youtube\.com/i,
            /youtu\.be/i,
            /\.mp4$/i,
            /\.webm$/i,
            /\.ogg$/i
        ];
        return videoPatterns.some(pattern => pattern.test(url));
    };

    // Helper function to check if a URL is a downloadable document
    const isDocumentUrl = (url: string | null): boolean => {
        if (!url) return false;
        const documentPatterns = [
            /\.pdf$/i,
            /\.doc$/i,
            /\.docx$/i,
            /\.xls$/i,
            /\.xlsx$/i,
            /\.ppt$/i,
            /\.pptx$/i,
            /\.csv$/i,
            /\.txt$/i,
            /\.zip$/i,
            /\.rar$/i
        ];
        return documentPatterns.some(pattern => pattern.test(url));
    };

    // Handler for button clicks
    const handleButtonClick = (item: FeaturedContentItem) => {
        if (!item.linkUrl) return;

        // If it's a video URL, open in lightbox
        if (isVideoUrl(item.linkUrl)) {
            setSelectedVideo({
                id: item.id,
                title: item.title,
                vimeoUrl: item.linkUrl
            });
        } else if (isDocumentUrl(item.linkUrl)) {
            // If it's a document, trigger download
            const link = document.createElement('a');
            link.href = item.linkUrl;
            link.download = ''; // Browser will use filename from URL
            link.target = '_blank'; // Fallback if download doesn't work
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // Otherwise, open URL in new tab (web pages, external links)
            window.open(item.linkUrl, '_blank', 'noopener,noreferrer');
        }
    };

    // Handler for image load
    const handleImageLoad = (itemId: number) => {
        setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
    };

    // Handler for image error
    const handleImageError = (itemId: number) => {
        setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
        setImageErrorStates(prev => ({ ...prev, [itemId]: true }));
    };

    // Loading spinner - only show on initial load, not on pagination changes
    if (initialLoading) {
        return (
            <div className="featured-content-loading">
                <div className="featured-content-loading-spinner"></div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="featured-content-error">
                <svg
                    className="featured-content-error-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                </svg>
                <p className="featured-content-error-text">{error}</p>
            </div>
        );
    }

    // Empty state
    if (!pageData || pageData.content.length === 0) {
        return (
            <div className="featured-content-empty">
                <svg
                    className="featured-content-empty-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                </svg>
                <p className="featured-content-empty-text">No featured content available at this time.</p>
            </div>
        );
    }

    return (
        <>
            <div className="featured-content-grid" ref={contentRef}>
                {pageData.content.map((item) => (
                    <div
                        key={item.id}
                        className="featured-content-card"
                        data-testid={`featured-content-${item.slug}`}
                    >
                        <div className="featured-content-card-body">
                            {/* Image and Content Container - Horizontal Layout */}
                            <div className="featured-content-header">
                                {/* Icon */}
                                <div className="featured-content-icon-wrapper">
                                    {imageLoadingStates[item.id] && !imageErrorStates[item.id] && (
                                        <div className="featured-content-icon-loading">
                                            <div className="featured-content-icon-spinner"></div>
                                        </div>
                                    )}
                                    <img
                                        src={imageErrorStates[item.id] ? "/api/placeholder/80/80" : item.featuredContentImageUrl}
                                        alt=""
                                        className="featured-content-icon"
                                        onLoad={() => handleImageLoad(item.id)}
                                        onError={() => handleImageError(item.id)}
                                    />
                                </div>

                                {/* Title and Meta Line */}
                                <div className="featured-content-text">
                                    {/* Subtitle Badge */}
                                    {item.subtitle && (
                                        <div className="featured-content-meta">
                                            <span className="featured-content-meta-badge">
                                                {item.subtitle}
                                            </span>
                                        </div>
                                    )}

                                    {/* Title */}
                                    <h3 className="featured-content-title">
                                        {decodeHtmlEntities(item.title)}
                                    </h3>
                                </div>
                            </div>

                            {/* CTA Button */}
                            <button
                                onClick={() => handleButtonClick(item)}
                                disabled={!item.linkUrl}
                                className={`featured-content-cta ${
                                    item.linkUrl
                                        ? "featured-content-cta-active"
                                        : "featured-content-cta-disabled"
                                }`}
                                aria-label={`${item.linkLabel} for ${item.title}`}
                                data-testid={`featured-content-action-${item.id}`}
                            >
                                {item.linkLabel}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Show pagination controls if enabled and there are multiple pages */}
            {showPagination && pageData.totalPages > 1 && (
                <Pagination
                    currentPage={pageData.number}
                    totalPages={pageData.totalPages}
                    totalElements={pageData.totalElements}
                    onPageChange={handlePageChange}
                />
            )}

            <VideoLightbox
                open={!!selectedVideo}
                resource={selectedVideo ?? undefined}
                onClose={() => setSelectedVideo(null)}
                analytics={selectedVideo ? featuredVideoAnalytics(WORDPRESS_TAB.NIL_EDU, selectedVideo.id) : null}
            />
        </>
    );
}
